import { type IntlShape } from 'react-intl';
import { type BasicPerson, type Person } from 'types';
import { type SuggestionGeneratorFunction } from 'views/Widgets/People/FilterablePeopleTable';
import {
  getEnhancedDescriptionForFieldName,
  PERSON_METADATA_NON_SENSITIVE_FIELD_IDS,
  personNonSensitiveColumnDescriptors,
} from 'views/Widgets/People/Table/commons';

interface FilterablePersonFields {
  function?: string;
  function_id?: string;
  cost_center?: string;
  cost_center_id?: string;
  business_unit?: string;
  business_unit_id?: string;
  department?: string;
  department_id?: string;
  location?: string;
  country?: string;
  manager?: BasicPerson | null;
  chain_of_command_list?: BasicPerson[];
}

export const personFilterOnlyColumns = (
  formatMessage: IntlShape['formatMessage']
): object[] => [
  {
    field: 'manager',
    name: getEnhancedDescriptionForFieldName('manager', formatMessage),
    isPerson: true,
    filterOnly: true,
    sortable: false,
  },
  {
    field: 'manager_or_above',
    name: getEnhancedDescriptionForFieldName('manager_or_above', formatMessage),
    isPerson: true,
    filterOnly: true,
    sortable: false,
  },
  ...personNonSensitiveColumnDescriptors(formatMessage, (column) => ({
    ...column,
    filterOnly: true,
  })),
];

// Row fields extracted off a person so the advanced filter can match on org
// attributes, the direct manager, and the chain of command. Spread onto each row.
export const extractPersonFilterFields = (
  person: Person
): Record<string, unknown> => {
  // metadata fields aren't on the base `Person` type but are serialized for
  // admins; read them defensively for the hidden filters
  const typedPerson = person as Person & Partial<FilterablePersonFields>;
  const metadataValues = PERSON_METADATA_NON_SENSITIVE_FIELD_IDS.reduce<
    Record<string, unknown>
  >((acc, field) => {
    acc[field] = (typedPerson as unknown as Record<string, unknown>)[field];
    return acc;
  }, {});
  const manager = typedPerson.manager ?? undefined;
  const chainOfCommand = typedPerson.chain_of_command_list ?? [];
  return {
    ...metadataValues,
    manager,
    // people in the chain of command, used to build the "manager or above"
    // suggestions and to match the corresponding filter
    manager_or_above: chainOfCommand,
    manager_or_above_ids: chainOfCommand
      .map((c) => c.id)
      .filter((cid): cid is number => cid !== undefined && cid !== null),
  };
};

export const personFilterSuggestionsGenerator: SuggestionGeneratorFunction = ({
  rows,
  valuesByColumnField,
  getItemFromField,
}) => {
  const visitedByField = Object.keys(valuesByColumnField).reduce<
    Record<string, Set<unknown>>
  >((agg, field) => {
    agg[field] = new Set();
    return agg;
  }, {});

  return rows.flatMap((row) => {
    const typedRow = row as Record<string, unknown>;
    const items: unknown[] = [];
    for (const field of Object.keys(valuesByColumnField)) {
      const value = typedRow[field];
      if (value === undefined || value === null || value === '') {
        continue;
      }
      switch (field) {
        case 'person':
        case 'manager': {
          // people dedupe by id
          const personId = (value as { id?: unknown }).id;
          if (personId === undefined || personId === null) {
            break;
          }
          if (!visitedByField[field].has(personId)) {
            visitedByField[field].add(personId);
            items.push(getItemFromField(field, value));
          }
          break;
        }
        case 'manager_or_above': {
          // one suggestion per person in the chain of command
          for (const manager of value as { id?: unknown }[]) {
            const personId = manager?.id;
            if (personId === undefined || personId === null) {
              continue;
            }
            if (!visitedByField[field].has(personId)) {
              visitedByField[field].add(personId);
              items.push(getItemFromField(field, manager));
            }
          }
          break;
        }
        default: {
          if (!visitedByField[field].has(value)) {
            visitedByField[field].add(value);
            items.push(getItemFromField(field, value));
          }
        }
      }
    }
    return items;
  });
};
